import { withScope } from '@sentry/react';
import { SENTRY_CONSTANTS, SentryIssueReportType } from 'utils/sentry';

/** Enum to identify the area of the app where the error occurred */
export enum AppErrorArea {
  unknown = 'unknown',
  signup = 'signup',
  login = 'login',
  dashboard = 'dashboard',
  billing = 'billing',
}

/** Tag key to be used in Sentry to identify the area of the app where the error occurred */
export const SENTRY_APP_AREA_TAG = 'app-area';

/** An expandible error class that can be used to add additional data to the error, including area where the error occurred */
export class DebuggableError extends Error {
  debugData: Record<string, unknown>;

  constructor(
    message: string,
    extra?: {
      debugData?: Record<string, unknown>;
      appArea?: AppErrorArea;
      reportType?: SentryIssueReportType;
    },
  ) {
    super(message);
    this.name = 'DebuggableError';
    this.debugData = extra?.debugData ?? {};
    this.debugData.area = extra?.appArea ?? AppErrorArea.unknown;

    withScope(scope => {
      scope.setTag(SENTRY_APP_AREA_TAG, extra?.appArea ?? AppErrorArea.unknown);
      scope.setTag(
        SENTRY_CONSTANTS.SENTRY_ISSUE_REPORT_TYPE_TAG_KEY,
        extra?.reportType ?? SentryIssueReportType.manualError,
      );
      scope.setExtra('debugData', extra?.debugData ?? {});

      scope.captureException(this);
    });
  }
}

/** Error specific to signup */
export class SignupError extends DebuggableError {
  constructor(message: string, extra?: { debugData?: Record<string, unknown> }) {
    super(message, { appArea: AppErrorArea.signup, ...extra });
  }

  static fromError(error: Error | DebuggableError) {
    if (error instanceof DebuggableError) {
      return error;
    }
    return new SignupError(error.message);
  }
}
